import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSettings } from '@ui/settings/use-settings';
import { apiClient } from '../http/query-client';
import { useBootstrapDataStore } from '@ui/bootstrap-data/bootstrap-data-store';
import { initFbc, getFbc, getFbp } from './facebook-track';

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

export function FacebookPixelTracker() {
  const { facebook } = useSettings();
  const location = useLocation();
  const pixelId = facebook?.pixel_id;
  const { user } = useBootstrapDataStore(s => s.data);
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Capture fbclid on mount, and re-sync _fbc/_fbp on every SPA route change
  // so a click-through that lands on one page and navigates client-side
  // still gets its fbclid captured / keeps storage in sync.
  useEffect(() => {
    initFbc();
  }, [location.pathname, location.search]);

  // Dynamically initialize the Meta Pixel base code if configured
  useEffect(() => {
    if (!pixelId) return;

    if (!window.fbq) {
      /* eslint-disable */
      (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
    }

    // Prepare advanced matching user details
    const userData: Record<string, any> = {};
    if (user) {
      userData.em = user.email;
      userData.external_id = String(user.id);
      
      const parts = (user.name || '').trim().split(' ');
      userData.fn = parts[0] || '';
      userData.ln = parts.length > 1 ? parts.slice(1).join(' ') : parts[0] || '';
      
      const savedPhone = typeof window !== 'undefined' ? localStorage.getItem('checkout_phone') : null;
      if (savedPhone) {
        userData.ph = savedPhone;
      }
    }

    window.fbq('init', pixelId, userData);
  }, [pixelId, user]);

  // Track PageView on route changes
  useEffect(() => {
    if (!pixelId || !window.fbq) return;

    const eventId = 'pv_' + Math.random().toString(36).substring(2, 15);
    const currentUser = userRef.current;
    
    const userDataPayload = {
      em: currentUser?.email,
      external_id: currentUser ? String(currentUser.id) : undefined,
      fn: currentUser ? (currentUser.name || '').trim().split(' ')[0] : undefined,
      ln: currentUser ? ((currentUser.name || '').trim().split(' ').length > 1 ? (currentUser.name || '').trim().split(' ').slice(1).join(' ') : (currentUser.name || '').trim().split(' ')[0]) : undefined,
      fbc: getFbc() || undefined,
      fbp: getFbp() || undefined,
      ph: (typeof window !== 'undefined' ? localStorage.getItem('checkout_phone') : null) || undefined,
    };

    // 1. Browser Event (Meta Pixel with Advanced Matching payload)
    window.fbq('track', 'PageView', {}, { eventID: eventId, ...userDataPayload });

    // 2. Server Event (Conversions API Proxy)
    apiClient.post('facebook/event', {
      event_name: 'PageView',
      event_id: eventId,
      event_source_url: window.location.href,
      user_data: userDataPayload,
    }).catch(() => {});
  }, [location.pathname, location.search, pixelId]);

  // Automatically track ViewContent on main landing/solution/pricing page loads
  useEffect(() => {
    if (!pixelId || !window.fbq) return;

    const path = location.pathname;
    const isLandingOrPricingOrSolution = 
      path === '/' || 
      path.startsWith('/pricing') ||
      path.includes('cloud-storage') ||
      path.includes('alternative');

    if (isLandingOrPricingOrSolution) {
      const eventId = 'vc_' + Math.random().toString(36).substring(2, 15);
      const contentName = path === '/' ? 'Home Page' : path.replace('/', '').replace(/-/g, ' ');
      const currentUser = userRef.current;

      const userDataPayload = {
        em: currentUser?.email,
        external_id: currentUser ? String(currentUser.id) : undefined,
        fn: currentUser ? (currentUser.name || '').trim().split(' ')[0] : undefined,
        ln: currentUser ? ((currentUser.name || '').trim().split(' ').length > 1 ? (currentUser.name || '').trim().split(' ').slice(1).join(' ') : (currentUser.name || '').trim().split(' ')[0]) : undefined,
        fbc: getFbc() || undefined,
        fbp: getFbp() || undefined,
        ph: (typeof window !== 'undefined' ? localStorage.getItem('checkout_phone') : null) || undefined,
      };

      // Browser Event
      window.fbq('track', 'ViewContent', {
        content_name: contentName,
      }, { eventID: eventId, ...userDataPayload });

      // Server Event (CAPI Proxy)
      apiClient.post('facebook/event', {
        event_name: 'ViewContent',
        event_id: eventId,
        event_source_url: window.location.href,
        user_data: userDataPayload,
        custom_data: {
          content_name: contentName,
        }
      }).catch(() => {});
    }
  }, [location.pathname, pixelId]);

  return null;
}

